import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../../styles/base-theme.css';
import Logo from '../ui/Logo';
import { db } from '../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import TiptapEditor from './TiptapEditor';

const zodiacSigns = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'
];

const tikkunDocuments = [
  'lilith', 'northnode', 'saturn', 'sun'
];

const AdminDashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = useState('lilith');
  const [selectedSign, setSelectedSign] = useState('aries');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHtmlCode, setShowHtmlCode] = useState(false);

  useEffect(() => {
    loadContent();
  }, [selectedDocument, selectedSign]);

  const COLLECTION_NAME = "tikkun";
  const loadContent = async () => {
    try {
      setLoading(true);
      setError('');

      console.log(selectedDocument, selectedSign);
      // Load content from Firestore using the new structure: /tikkunCollection/{selectedDocument}/signs/{selectedSign}
      const docRef = doc(db, COLLECTION_NAME, selectedDocument, "signs", selectedSign);
      const docSnap = await getDoc(docRef);

      console.log(docSnap.data());

      if (docSnap.exists()) {
        const data = docSnap.data();
        // The content is stored directly in the document
        setContent(data.content || '');
      } else {
        // If document doesn't exist, set empty content
        setContent('');
        console.log(`No content found for ${selectedDocument}/signs/${selectedSign}`);
      }
    } catch (error) {
      console.error('Error loading content:', error);
      setError('Erro ao carregar o conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Save content to Firestore using the new structure: /tikkunCollection/{selectedDocument}/signs/{selectedSign}
      const docRef = doc(db, COLLECTION_NAME, selectedDocument, "signs", selectedSign);
      await setDoc(docRef, {
        content: content,
        lastUpdated: new Date().toISOString(),
        updatedBy: currentUser?.email || 'unknown'
      });

      setSuccess('Conteúdo salvo com sucesso!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving content:', error);
      setError('Erro ao salvar o conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };



  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Logo />

            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Logado como: {currentUser?.email}
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        <div className="p-4">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Editar Conteúdo Tikkun
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Selecione o documento e signo para editar o conteúdo correspondente.
              </p>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                  {success}
                </div>
              )}

              <div className="space-y-6">
                {/* Document and Sign Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Documento
                    </label>
                    <select
                      value={selectedDocument}
                      onChange={(e) => setSelectedDocument(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      {tikkunDocuments.map(doc => (
                        <option key={doc} value={doc}>
                          {doc.charAt(0).toUpperCase() + doc.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Signo
                    </label>
                    <select
                      value={selectedSign}
                      onChange={(e) => setSelectedSign(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      {zodiacSigns.map(sign => (
                        <option key={sign} value={sign}>
                          {sign.charAt(0).toUpperCase() + sign.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Content Editor */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Conteúdo para {selectedDocument} em {selectedSign}
                    </label>
                    <button
                      onClick={() => setShowHtmlCode(!showHtmlCode)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                    >
                      {showHtmlCode ? 'Ver Editor' : 'Ver HTML'}
                    </button>
                  </div>
                  {loading ? (
                    <div className="flex items-center justify-center h-64 border border-gray-300 rounded-md">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      <span className="ml-3 text-gray-600">Carregando...</span>
                    </div>
                  ) : showHtmlCode ? (
                    <div className="border border-gray-300 rounded-md h-[400px]">
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Digite o HTML aqui..."
                      />
                    </div>
                  ) : (
                    <div className="rounded-md h-[400px] border border-gray-300">
                      <TiptapEditor
                        content={content}
                        onChange={setContent}
                        placeholder="Digite o conteúdo aqui..."
                      />
                    </div>
                  )}
                </div>

                {/* Content Preview */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview do Conteúdo (como aparecerá no PDF)
                  </label>
                  <div className="border border-gray-300 rounded-md p-6 bg-white min-h-[200px] max-h-[400px] overflow-y-auto">
                    <div 
                      className="base-content"
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  </div>
                </div> */}

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving || loading}
                    className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-6 py-2 rounded-md font-medium"
                  >
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 